import React, { useState, useEffect, useContext, useMemo } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom"; 
import "./Animaux.css"; 
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { PageContainer, PageTitleBadge, DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import { GiCow } from "react-icons/gi";
import { FaPlus, FaArrowLeft } from "react-icons/fa";
import { hasPermission, PERMISSIONS } from "../utils/permissions";




export default function Animaux() {
  const { user } = useContext(UserContext);
  const { t, lang } = useTranslation();
  const [animaux, setAnimaux] = useState([]);
  const [troupeaux, setTroupeaux] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [races, setRaces] = useState([]);
  const [robes, setRobes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // États pour les filtres
  const [sexe, setSexe] = useState('');
  const [statut, setStatut] = useState('');
  const [race, setRace] = useState('');
  const [categorie, setCategorie] = useState('');
  const [troupeau, setTroupeau] = useState('');

  // État pour le champ de recherche (search_fields)
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (0.8 = 80% à 1.5 = 150%, valeur par défaut 1.0 = 100%)
  // Gardé pour les colonnes qui utilisent zoomLevel dans leurs styles
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('animaux-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  // Sauvegarder le niveau de zoom dans localStorage
  useEffect(() => {
    localStorage.setItem('animaux-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    console.log("Utilisateur connecté :", user); // 🔎 Debug ici
  }, [user]); // 

  // Charger les ranches et les catégories une seule fois
  useEffect(() => {
    const fetchRanches = async () => {
      try {
        const response = await api.get("/api/ranches/");
        setRanches(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des ranches:", err);
      }
    };
    fetchRanches();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get("/api/categories/");
        setCategories(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des catégories:", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const response = await api.get("/api/races/");
        setRaces(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des races:", err);
      }
    };
    fetchRaces();
  }, []);

  useEffect(() => {
    const fetchRobes = async () => {
      try {
        const response = await api.get("/api/robes/");
        setRobes(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des robes:", err);
      }
    };
    fetchRobes();
  }, []);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
         // Construction des paramètres de la requête
      const troupeauxResponse = await api.get("/api/troupeaux/");
      setTroupeaux(troupeauxResponse.data);
      const params = new URLSearchParams();
      if (sexe) params.append('sexe', sexe);
      if (statut) params.append('statut', statut);
      if (race) params.append('race__icontains', race); // ou 'exact' selon le cas
      if (categorie) params.append('categorie', categorie);
      if (troupeau) params.append('troupeau', troupeau);
      if (searchTerm) params.append('search', searchTerm);
       
      
      const response = await api.get("/api/animaux/", { params}); 
        setAnimaux(response.data);
        setLoading(false);
      } catch (err) {
        setError(t.errorLoadingAnimals);
        setLoading(false);
      }
    };
    fetchAnimaux();
  }, [sexe, statut, race, categorie, troupeau, searchTerm]);

  const getStatusClass = (statut) => {
    switch (statut) {
      case 'actif':
        return 'status-actif';
      case 'mort':
        return 'status-mort';
      case 'zakat':
        return 'status-zakat';
      case 'vendu':
        return 'status-vendu';
      default:
        return ''; // Pas de classe spéciale pour les autres statuts
    }
  };


  // Fonction pour obtenir le nom du ranch de provenance
  const getRanchProvenanceName = (ranchId) => {
    if (!ranchId) return t.notProvided;
    // Si ranchId est un objet avec nom
    if (typeof ranchId === 'object' && ranchId.nom) {
      return ranchId.nom;
    }
    // Si ranchId est un ID (nombre), chercher dans la liste des ranches
    if (typeof ranchId === 'number') {
      const ranch = ranches.find(r => r.id === ranchId);
      if (ranch) {
        return ranch.nom || t.notProvided;
      }
    }
    return t.notProvided;
  };

  // Fonction pour obtenir le matricule du géniteur (père)
  const getGeniteurMatricule = (pereId) => {
    if (!pereId) return t.notAvailable;
    // Si pereId est un objet avec matricule
    if (typeof pereId === 'object' && pereId.matricule) {
      return pereId.matricule;
    }
    // Si pereId est un ID (nombre), chercher dans la liste des animaux
    if (typeof pereId === 'number') {
      const pereAnimal = animaux.find(a => a.id === pereId);
      if (pereAnimal) {
        return pereAnimal.matricule || t.notAvailable;
      }
    }
    return t.notAvailable;
  };

  // Fonction pour obtenir le matricule de la génitrice (mère)
  const getGenitriceMatricule = (mereId) => {
    if (!mereId) return t.notAvailable;
    // Si mereId est un objet avec matricule
    if (typeof mereId === 'object' && mereId.matricule) {
      return mereId.matricule;
    }
    // Si mereId est un ID (nombre), chercher dans la liste des animaux
    if (typeof mereId === 'number') {
      const mereAnimal = animaux.find(a => a.id === mereId);
      if (mereAnimal) {
        return mereAnimal.matricule || t.notAvailable;
      }
    }
    return t.notAvailable;
  };

  // Fonction pour obtenir le nom de la race
  const getRaceName = (raceId) => {
    if (!raceId) return t.notProvided;
    // Si raceId est un objet avec nom
    if (typeof raceId === 'object' && raceId.nom) {
      return raceId.nom;
    }
    // Si raceId est un ID (nombre), chercher dans la liste des races
    if (typeof raceId === 'number') {
      const race = races.find(r => r.id === raceId);
      if (race) {
        return race.nom || t.notProvided;
      }
    }
    return t.notProvided;
  };

  // Fonction pour obtenir le nom de la robe
  const getRobeName = (robeId) => {
    if (!robeId) return t.notProvided;
    // Si robeId est un objet avec nom
    if (typeof robeId === 'object' && robeId.nom) {
      return robeId.nom;
    }
    // Si robeId est un ID (nombre), chercher dans la liste des robes
    if (typeof robeId === 'number') {
      const robe = robes.find(r => r.id === robeId);
      if (robe) {
        return robe.nom || t.notProvided;
      }
    }
    return t.notProvided;
  };

  // Configuration des colonnes pour DataGrid
  const columns = useMemo(() => {
    const baseColumns = [
      { 
        field: "matricule", 
        headerName: t.matricule, 
        flex: 1, 
        minWidth: 140,
        headerAlign: 'left',
        align: 'left',
      },
      { 
        field: "sexe", 
        headerName: t.sex, 
        width: 90,
        headerAlign: 'center',
        align: 'center',
      },
      { 
        field: "age", 
        headerName: t.age, 
        width: 90, 
        type: "number",
        headerAlign: 'center',
        align: 'center',
      },
      { 
        field: "race", 
        headerName: t.breed, 
        flex: 1, 
        minWidth: 140,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (value, row) => {
          return getRaceName(row?.race);
        },
      },
      { 
        field: "robe", 
        headerName: t.coat, 
        flex: 1, 
        minWidth: 140,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (value, row) => {
          return getRobeName(row?.robe);
        },
      },
      {
        field: "statut",
        headerName: t.status,
        width: 130,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <span 
            className={getStatusClass(params.value)}
            style={{ fontSize: `${1.3 * zoomLevel}rem` }}
          >
            {params.value}
          </span>
        ),
      },
      { 
        field: "categorie", 
        headerName: t.category, 
        flex: 1, 
        minWidth: 140,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (value, row) => row?.categorie_nom || row?.categorie || t.notProvided,
      },
      { 
        field: "valeur", 
        headerName: t.value, 
        width: 110, 
        type: "number",
        headerAlign: 'right',
        align: 'right',
      },
      {
        field: "ranch_provenance",
        headerName: t.originRanch,
        flex: 1,
        minWidth: 160,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (value, row) => {
          return getRanchProvenanceName(row?.ranch_provenance);
        },
      },
      {
        field: "date_naissance",
        headerName: t.date,
        width: 130,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value, row) => {
          if (!row?.date_naissance) return t.notAvailable;
          const date = new Date(row.date_naissance);
          return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US');
        },
      },
    ];

    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_ANIMAUX);
    
    const adminColumns = canEdit
      ? [
          {
            field: "edit",
            headerName: t.actions,
            width: 130,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
              const isDisabled = params.row.statut === "vendu" || params.row.statut === "mort";
              return (
                <button 
                  onClick={() => !isDisabled && navigate(`/animaux/${params.row.id}/modifier`)}
                  className="grid-action-button"
                  disabled={isDisabled}
                  style={{ 
                    fontSize: `${1.3 * zoomLevel}rem`,
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                  title={isDisabled ? t.cannotEditSoldOrDead : ""}
                >
                  {t.edit}
                </button>
              );
            },
          },
        ]
      : [];

    const detailsColumn = [
      {
        field: "details",
        headerName: t.details,
        width: 130,
        sortable: false,
        filterable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <button 
            onClick={() => {
              console.log("Navigation vers fiche animal, ID:", params.row.id);
              navigate(`/animaux/${params.row.id}/fiche`);
            }}
            className="grid-action-button"
            style={{ fontSize: `${1.3 * zoomLevel}rem` }}
          >
            {t.viewMore}
          </button>
        ),
      },
    ];

    return [...baseColumns, ...detailsColumn, ...adminColumns];
  }, [user, navigate, zoomLevel, ranches, animaux, races, robes, t, lang]);

  return (
    <PageContainer>
      <PageTitleBadge title={t.animalList} icon={<GiCow />} />
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_ANIMAUX)) && (
        <button onClick={() => navigate("/ajouter-animal")} className="add-animal-button">
          <FaPlus />
          {t.addAnimal}
        </button>
      )}
        {loading && <p>{t.loading}</p>}
        {error && (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button 
              onClick={() => navigate(-1)} 
              className="back-button"
              style={{ marginTop: '1rem' }}
            >
              <FaArrowLeft /> {t.back}
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="animaux-container">
            <div className="top-section">
              <SearchBar
                placeholder={t.searchByMatricule}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="default"
              />
            </div>
            <FiltersContainer title={t.filters}>
              <FilterSelect
                name="sexe"
                value={sexe}
                onChange={(e) => setSexe(e.target.value)}
                options={[
                  { value: "M", label: t.male },
                  { value: "F", label: t.female }
                ]}
                placeholder={t.filterBySex}
                className="filtre-sexe"
                width="150px"
              />
              <FilterSelect
                name="statut"
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                options={[
                  { value: "actif", label: t.active },
                  { value: "vendu", label: t.sold },
                  { value: "mort", label: t.dead }
                ]}
                placeholder={t.filterByStatus}
                className="filtre-statut"
                width="180px"
              />
              <FilterSelect
                name="categorie"
                value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
                options={categories.map(cat => ({ id: cat.id, nom: cat.nom }))}
                placeholder={t.filterByCategory}
                className="filtre-categorie"
                width="200px"
              />
              <FilterSelect
                name="troupeau"
                value={troupeau}
                onChange={(e) => setTroupeau(e.target.value)}
                options={troupeaux.map(troupeau => ({ id: troupeau.id, nom: troupeau.nom }))}
                placeholder={t.filterByHerd}
                className="filtre-troupeau"
                width="180px"
              />
            </FiltersContainer>
            {/* DataGrid moderne pour les animaux */}
            {animaux.length === 0 ? (
              <div className="empty-message-container">
                <p className="empty-message">
                  {sexe || statut || categorie || troupeau || searchTerm
                    ? t.noAnimalsFound
                    : t.noAnimalsRegistered}
                </p>
              </div>
            ) : (
              <DataGridTable
                rows={animaux}
                columns={columns}
                loading={loading}
                storageKey="animaux-zoom-level"
                zoomLevel={zoomLevel}
                onZoomChange={setZoomLevel}
                initialSort={{ field: 'matricule', sort: 'asc' }}
                pageSize={25}
                containerClassName="animal-grid-container"
              />
            )}
          </div>

        )}
    </PageContainer>
  );
}